import React, { useState, useEffect } from "react"
import { Box, IconButton } from "@mui/material"
import FmdBadIcon from "@mui/icons-material/FmdBad"
import InfoIcon from "@mui/icons-material/Info"
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined"
import RouteIcon from "@mui/icons-material/Route"
import { motion, AnimatePresence } from "framer-motion"
import HamburgerLogo from "../HamburgerLogo"
import { useTheme } from "@mui/material/styles"
import { EJobStatus } from "../../entities/job/interface"

type HamburgerMenuProps = {
    onIconSelect: (iconName: string) => void
    isGroupJob: boolean
    jobStatus: EJobStatus // Add jobStatus to props
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ onIconSelect, isGroupJob, jobStatus }) => {
    const [iconsTop, setIconsTop] = useState(300) // Default Top for Icons
    const [isMenuExpanded, setMenuExpanded] = useState(
        jobStatus === EJobStatus.onRouteToPickup || jobStatus === EJobStatus.onRouteToDelivery
    )
    const theme = useTheme()

    const handleMenuToggle = () => setMenuExpanded((prev) => !prev)

    const icons = [
        { icon: FmdBadIcon, name: "alert", color: "error.orange" },
        { icon: InfoIcon, name: "info", color: "info.dark" },
        { icon: ArticleOutlinedIcon, name: "documents", color: "info.dark" },
        { icon: RouteIcon, name: "roadmap", color: "info.dark" },
    ]

    // Ensure menu opens for Pickup & Delivery, closes otherwise
    useEffect(() => {
        if (jobStatus === EJobStatus.onRouteToPickup || jobStatus === EJobStatus.onRouteToDelivery) {
            setMenuExpanded(true)
        } else {
            setMenuExpanded(false)
        }
    }, [jobStatus])

    useEffect(() => {
        const updatePositions = () => {
            const screenHeight = window.innerHeight
            // setHamburgerTop(screenHeight * 0.05) // ✅ Dynamic top 5% of screen height
            setIconsTop(screenHeight * 0.35) // ✅ Dynamic top 35% of screen height
        }
        updatePositions()
        window.addEventListener("resize", updatePositions)

        return () => window.removeEventListener("resize", updatePositions)
    }, [])

    // Remove first icon if jobStatus is NOT onRouteToPickup
    const filteredIcons =
        jobStatus === EJobStatus.onRouteToPickup || jobStatus === EJobStatus.onRouteToDelivery ? icons : icons.slice(1) // ✅ Removes first icon if status is not Pickup or Delivery

    // Apply the group job condition
    const displayedIcons =
        isGroupJob && (jobStatus === EJobStatus.onRouteToPickup || jobStatus === EJobStatus.onRouteToDelivery)
            ? filteredIcons
            : filteredIcons.slice(0, -1) // Removes the last icon if not a group job

    return (
        <>
            {/* ✅ 1. Separate Box for Hamburger Button */}
            <Box position="fixed" zIndex={10} right={5} top={50}>
                <IconButton onClick={handleMenuToggle} size="large" sx={{ borderRadius: "50%" }}>
                    <HamburgerLogo size={50} />
                </IconButton>
            </Box>
            {/* ✅ 2. Separate Box for Icon Buttons */}
            <Box
                position="fixed"
                zIndex={9}
                right={15}
                top={"40%"}
                sx={{
                    top: `${iconsTop}px`, // ✅ Dynamic position based on screen height
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <AnimatePresence>
                    {isMenuExpanded && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            {displayedIcons.map((item, index) => (
                                <motion.div
                                    key={item.name}
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.1 * index, duration: 0.3 }}
                                    style={{
                                        marginTop: theme.spacing(1),
                                    }}
                                >
                                    <IconButton
                                        onClick={() => onIconSelect(item.name)}
                                        size="large"
                                        sx={{
                                            backgroundColor: item.color,
                                            borderRadius: "50%",
                                            "&:hover": { backgroundColor: item.color },
                                            "&:focus": { backgroundColor: item.color },
                                            "&:active": { backgroundColor: item.color },
                                        }}
                                    >
                                        <item.icon sx={{ fontSize: 30, color: "text.primary" }} />
                                    </IconButton>
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>
        </>
    )
}

export default HamburgerMenu
